"use client";

import { Sheet, SheetContent, SheetHeader, SheetOverlay } from "@/components/rac/sheet";
import { USER_RELATION_MUTATION } from "@/gql/user-mutations";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserStore } from "@/stores/user-store";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import { saveGroupPreference } from "@/utils/user-preferences";
import { useMutation } from "@apollo/client";
import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";
import { RelationshipDialogContent } from "./relationship-dialog-content";
import { UserGroup } from "./types";
export interface RelationshipDialogProps {
  isOpen: boolean;
  onClose: () => void;

  // Mode configuration
  mode?: "selection" | "guard";

  // Guard mode props
  requiredRelationship?: "buyer" | "seller";
  featureName?: string;
  description?: string;
  currentRelationship?: "buyer" | "seller";

  // Selection mode props
  onComplete?: (relationship: "buyer" | "seller", groupId?: string) => void;
  targetRelationship?: "buyer" | "seller";
}
export function RelationshipDialog({
  isOpen,
  onClose,
  mode = "selection",
  requiredRelationship,
  targetRelationship,
  featureName = "This Feature",
  description,
  currentRelationship: propCurrentRelationship,
  onComplete
}: RelationshipDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [step, setStep] = useState<"initial" | "group-selection">("initial");
  const [pendingRelationship, setPendingRelationship] = useState<"buyer" | "seller" | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedRelationship, setSelectedRelationship] = useState<"buyer" | "seller" | null>(null);
  const {
    user,
    refetchUser
  } = useAuth();
  const isMobile = useIsMobile();

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);
  const [updateUserRelation] = useMutation(USER_RELATION_MUTATION, {
    refetchQueries: ["GetUser"],
    awaitRefetchQueries: true
  });

  // Determine effective mode based on props interaction
  const effectiveMode = requiredRelationship ? "guard" : mode;
  const currentRelationship = propCurrentRelationship || (user?.relation === "buyer" || user?.relation === "seller" ? user.relation : undefined);

  // Reset state on open/close
  useEffect(() => {
    if (isOpen) {
      if (targetRelationship) {
        setStep("group-selection");
        setPendingRelationship(targetRelationship);
      } else {
        setStep("initial");
        setPendingRelationship(null);
      }
      setSelectedGroupId(null);
      setIsLoading(false);
      setSelectedRelationship(null);
    }
  }, [isOpen, targetRelationship]);

  // Group users by relationship
  const groupsByRelationship = useMemo(() => {
    if (!user?.groups) return {
      buyer: [] as UserGroup[],
      seller: [] as UserGroup[]
    };
    const grouped = (user.groups as UserGroup[]).reduce((acc, group) => {
      if (group.relationship === "buyer") acc.buyer.push(group);
      if (group.relationship === "seller") acc.seller.push(group);
      return acc;
    }, {
      buyer: [] as UserGroup[],
      seller: [] as UserGroup[]
    });
    return grouped;
  }, [user?.groups]);
  const performSwitch = useCallback(async (newRelationship: "buyer" | "seller", groupId?: string) => {
    if (!user) return;

    // Set visual feedback immediately
    setSelectedRelationship(newRelationship);
    setIsLoading(true);
    try {
      const {
        data
      } = await updateUserRelation({
        variables: {
          params: {
            update: {
              relationship: newRelationship
            }
          }
        }
      });
      if (data?.user?.update) {
        // Save group preference if a specific group was chosen or inferred
        const finalGroupId = groupId || groupsByRelationship[newRelationship]?.[0]?.id;
        if (finalGroupId) {
          await saveGroupPreference(finalGroupId, newRelationship);

          // Update global store so data hooks use the correct group
          useUserStore.getState().setSelectedGroup({
            selectedGroupId: finalGroupId,
            relationship: newRelationship
          });
        }
        await refetchUser(data.user.update);
        const msg = groupId ? `Switched to ${newRelationship} mode with selected group` : `Switched to ${newRelationship} mode`;
        toast.success(msg);
        onComplete?.(newRelationship, groupId);
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        throw new Error("Failed to update relationship");
      }
    } catch (error) {
      logger.error("Failed to switch relationship:", error);
      toast.error("Failed to update relationship. Please try again.");
      setSelectedRelationship(null); // Reset on error
    } finally {
      setIsLoading(false);
    }
  }, [user, updateUserRelation, refetchUser, groupsByRelationship, onComplete, onClose]);
  const handleRelationshipSwitch = useCallback(async (newRelationship_0: "buyer" | "seller") => {
    if (!user || isLoading) return;

    // If already active in guard mode, do nothing
    if (currentRelationship === newRelationship_0 && effectiveMode === "guard") return;

    // Check groups
    const targetGroups = groupsByRelationship[newRelationship_0] || [];
    if (targetGroups.length > 1) {
      // Multiple groups logic
      setPendingRelationship(newRelationship_0);
      setStep("group-selection");
      return;
    }

    // Single or no group logic
    let groupId_0 = undefined;
    if (targetGroups.length === 1) {
      groupId_0 = targetGroups[0].id;
    }
    await performSwitch(newRelationship_0, groupId_0);
  }, [user, isLoading, currentRelationship, effectiveMode, groupsByRelationship, performSwitch]);
  const handleGroupSelect = useCallback(async (groupId_1: string) => {
    if (pendingRelationship) {
      setSelectedGroupId(groupId_1);
      await performSwitch(pendingRelationship, groupId_1);
    }
  }, [pendingRelationship, performSwitch]);
  const handleBack = useCallback(() => {
    setStep("initial");
    setPendingRelationship(null);
    setSelectedGroupId(null);
  }, []);
  if (!isOpen || !mounted) return null;

  // --- Render Content ---

  const content = <RelationshipDialogContent step={step} pendingRelationship={pendingRelationship} groupsByRelationship={groupsByRelationship} isLoading={isLoading} selectedGroupId={selectedGroupId} selectedRelationship={selectedRelationship} effectiveMode={effectiveMode} mode={mode} requiredRelationship={requiredRelationship} currentRelationship={currentRelationship} featureName={featureName} description={description} onClose={onClose} onBack={handleBack} onGroupSelect={handleGroupSelect} onRelationshipSwitch={handleRelationshipSwitch} />;

  // --- Layout Logic ---

  if (isMobile) {
    return <>
				<div className="fixed inset-0 z-[99998] pointer-events-auto bg-transparent" onClick={e => e.stopPropagation()} />
				<Sheet isOpen={isOpen} onOpenChange={onClose}>
					<SheetOverlay className="z-[99999] bg-black/40" isDismissable={effectiveMode === "selection" && !isLoading}>
						<div className="relative pointer-events-auto">
							<button type="button" onClick={e_0 => {
              e_0.stopPropagation();
              onClose();
            }} className="absolute right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors z-[100001]" style={{
              top: "calc(15vh - 20px)"
            }} aria-label="Close modal">
								<X className="h-6 w-6 text-gray-800" />
							</button>
						</div>
						<SheetContent side="bottom" className="max-h-[85vh] min-h-[60vh] rounded-t-2xl bg-white flex flex-col border-0 shadow-2xl z-[100000] pointer-events-auto" closeButton={false}>
							<div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
								<SheetHeader className="relative flex-shrink-0 pb-6">
									<div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-6 md:hidden" />
									{content}
								</SheetHeader>
							</div>
						</SheetContent>
					</SheetOverlay>
				</Sheet>
			</>;
  }
  return <ModalOverlay isOpen={isOpen} onOpenChange={isOpen_0 => {
    if (!isOpen_0) onClose();
  }} isDismissable={effectiveMode === "selection" && !isLoading} className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center">
			<Modal className="relative bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden outline-none">
				<Dialog className="outline-none">
					<div className={cn("relative", effectiveMode === "selection" && step === "initial" ? "p-0" : "p-6")}>
						<button type="button" onClick={e_1 => {
            e_1.stopPropagation();
            onClose();
          }} className={cn("absolute right-4 top-4 p-1 rounded-full hover:bg-gray-100 transition-colors z-10", effectiveMode === "selection" ? "text-primary-900" : "text-gray-600")} aria-label="Close modal">
							<X className="h-5 w-5" />
						</button>
						{content}
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>;
}